import React, { useMemo, useState } from "react";
import { Entity } from "@common/types/entity";
import { ExcludeEmptyMockEntitiesMode } from "@hooks/useEntitiesContexts";
import { useEntities } from "@hooks/useEntities";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemValue } from "@design-system/Picker/Picker.types";
import { EntityFilterProps, EntityFilterType, NO_ENTITY_VALUE } from "./types";
import { EntityFilterSingleCombobox } from "./EntityFilterSingleCombobox";
import { EntityFilterTrigger } from "./EntityFilterTrigger";
import { EntityFilterPopup, groupEntitiesForFilter } from "./EntityFilterPopup";

const NEVER_DISABLED = () => false;

interface EntityFilterSelection {
    entityIds: number[];
    hasNoEntity: boolean;
}

/**
 * Multi-entity chooser: Business / Personal sections, each with a select-all
 * over its matching enabled rows. Picks show at once through a local draft
 * (the caller's `filters` may lag) that resets when the popup closes. In
 * `filter` mode an empty pick means every entity; in `selection` mode it
 * means none. `mutuallyExclusive` keeps picks within one section.
 * `showNoEntityOption` appends a "No entity" row that maps to `hasNoEntity`.
 */
const EntityFilterMultiCombobox: React.FC<EntityFilterProps> = ({
    filters,
    onChange,
    onlyBusiness,
    container,
    showLabel,
    excludeAllMockEntities,
    excludeEmptyMockEntities = ExcludeEmptyMockEntitiesMode.REQUIRE_ACCOUNTS,
    mode = "filter",
    mutuallyExclusive,
    showNoEntityOption = false,
    disabled = false,
    isEntityDisabled = NEVER_DISABLED,
    disabledEntityTooltip,
    triggerButtonProps,
    "aria-labelledby": ariaLabelledby,
    popupLabel,
    renderTrigger,
    open,
    onOpenChange,
    onBlur,
    invalid,
    ref,
}) => {
    const [search, setSearch] = useState("");
    const [draftSelection, setDraftSelection] = useState<
        EntityFilterSelection | undefined
    >();
    const entities = useEntities({
        excludeEmptyMockEntities,
        excludeMockEntities: excludeAllMockEntities,
    });
    const entitiesById = useMemo(
        () => new Map(entities.map((entity) => [entity.id, entity])),
        [entities],
    );

    const selectedEntityIds = useMemo(
        () => draftSelection?.entityIds ?? filters.entityIds ?? [],
        [draftSelection, filters.entityIds],
    );
    const isNoEntitySelected =
        showNoEntityOption &&
        (draftSelection?.hasNoEntity ?? filters.hasNoEntity === true);
    const knownSelectedEntityIds = useMemo(
        () => selectedEntityIds.filter((id) => entitiesById.has(id)),
        [selectedEntityIds, entitiesById],
    );
    const pickerValue = useMemo<PickerItemValue[]>(
        () =>
            isNoEntitySelected
                ? [...knownSelectedEntityIds, NO_ENTITY_VALUE]
                : knownSelectedEntityIds,
        [knownSelectedEntityIds, isNoEntitySelected],
    );

    const emptyEntityIds = mode === "selection" ? [] : undefined;
    const isOnlyNoEntitySelected =
        isNoEntitySelected && knownSelectedEntityIds.length === 0;

    const selectedEntities = useMemo(() => {
        let entitiesToReturn = entities.filter((entity) =>
            selectedEntityIds.includes(entity.id),
        );
        if (selectedEntityIds.length === 0) {
            entitiesToReturn =
                mode === "selection" || isOnlyNoEntitySelected ? [] : entities;
        }

        return onlyBusiness
            ? entitiesToReturn.filter(({ isBusiness }) => isBusiness)
            : entitiesToReturn;
    }, [
        entities,
        selectedEntityIds,
        mode,
        onlyBusiness,
        isOnlyNoEntitySelected,
    ]);

    const { businessEntities, personalEntities } = useMemo(
        () => groupEntitiesForFilter({ entities, onlyBusiness, search }),
        [entities, onlyBusiness, search],
    );
    const selectableIdsOf = (groupEntities: Entity[]) =>
        groupEntities
            .filter((entity) => !isEntityDisabled(entity))
            .map(({ id }) => id);

    const applySelection = ({
        entityIds: newEntityIds,
        hasNoEntity,
    }: EntityFilterSelection) => {
        setDraftSelection({ entityIds: newEntityIds, hasNoEntity });
        const nextFilters: EntityFilterType = {
            entityIds: newEntityIds.length > 0 ? newEntityIds : emptyEntityIds,
        };
        if (showNoEntityOption) {
            nextFilters.hasNoEntity = hasNoEntity || undefined;
        }
        onChange(nextFilters);
    };

    const handleValueChange = (nextValues: PickerItemValue[]) => {
        let pickedEntities = nextValues.flatMap((value) => {
            const entity =
                typeof value === "number" ? entitiesById.get(value) : undefined;
            return entity && !isEntityDisabled(entity) ? [entity] : [];
        });

        if (mutuallyExclusive) {
            const addedEntity = pickedEntities.find(
                (entity) => !selectedEntityIds.includes(entity.id),
            );
            if (addedEntity) {
                pickedEntities = pickedEntities.filter(
                    (entity) => entity.isPersonal === addedEntity.isPersonal,
                );
            }
        }

        applySelection({
            entityIds: pickedEntities.map(({ id }) => id),
            hasNoEntity:
                showNoEntityOption && nextValues.includes(NO_ENTITY_VALUE),
        });
    };

    const selectableEntities = entities.filter(
        (entity) =>
            (!onlyBusiness || entity.isBusiness) && !isEntityDisabled(entity),
    );
    const isEverySelected =
        selectableEntities.length > 0 &&
        selectableEntities.every((entity) =>
            selectedEntityIds.includes(entity.id),
        ) &&
        (!showNoEntityOption || isNoEntitySelected);
    const selectedCount =
        selectedEntityIds.length + (isNoEntitySelected ? 1 : 0);

    return (
        <Picker.Select
            multiple
            searchable
            value={pickerValue}
            onValueChange={handleValueChange}
            onInputValueChange={setSearch}
            open={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    setSearch("");
                    setDraftSelection(undefined);
                }
                onOpenChange?.(isOpen);
            }}
            disabled={disabled}
            aria-invalid={invalid}
        >
            <EntityFilterTrigger
                renderTrigger={renderTrigger}
                selectedEntities={selectedEntities}
                isOnlyNoEntitySelected={isOnlyNoEntitySelected}
                disabled={disabled}
                showLabel={showLabel}
                triggerButtonProps={triggerButtonProps}
                aria-labelledby={ariaLabelledby}
                invalid={invalid}
                ref={ref}
                onBlur={onBlur}
            />
            <EntityFilterPopup
                container={container}
                aria-label={popupLabel}
                groups={[
                    {
                        title: "Business",
                        entities: businessEntities,
                        selectAllValues: selectableIdsOf(businessEntities),
                    },
                    {
                        title: "Personal",
                        entities: personalEntities,
                        selectAllValues: selectableIdsOf(personalEntities),
                    },
                ]}
                isEntityDisabled={isEntityDisabled}
                disabledEntityTooltip={disabledEntityTooltip}
                selectedCount={isEverySelected ? 0 : selectedCount}
                onDeselect={() =>
                    applySelection({ entityIds: [], hasNoEntity: false })
                }
                showNoEntityOption={showNoEntityOption}
                search={search}
            />
        </Picker.Select>
    );
};

export const EntityFilterCombobox: React.FC<EntityFilterProps> = (props) =>
    props.selectionMode === "single" ? (
        <EntityFilterSingleCombobox {...props} />
    ) : (
        <EntityFilterMultiCombobox {...props} />
    );
